import { TFunction } from 'i18next';
import { z } from 'zod';
import { Language } from '../../locales/Language';
import { TdPrefix, TdSupportedCountriesOfResidence } from './FormStateMapper';
import {
  DEFAULT_MIN_STRING_LENGTH,
  TD_COUNTRY_CODE_LENGTH,
  TD_FIELD_MAX_LENGTH,
  TD_NATIONAL_PHONE_LENGTH,
} from './constants';

const lettersSpacesAndHyphensRegex = /^[[a-zA-Z -]*$/g;
const TD_MOBILE_PHONE_FIELD_LENGTH = 21;

export const nameSchema = (t: TFunction) =>
  z
    .string({
      required_error: t('td.errors.nameIsRequiredAndMustBeValid'),
      invalid_type_error: t('td.errors.nameIsRequiredAndMustBeValid'),
    })
    .trim()
    .max(
      TD_FIELD_MAX_LENGTH,
      t('td.errors.maxLength', {
        maxLength: TD_FIELD_MAX_LENGTH,
      }),
    )
    .refine(
      value => !!value.match(lettersSpacesAndHyphensRegex),
      t('td.errors.nameIsRequiredAndMustBeValid'),
    );

export const phoneNumberSchema = (t: TFunction) =>
  z
    .string({
      required_error: t('td.errors.mobileNumberIsRequiredAndMustBeValid'),
      invalid_type_error: t('td.errors.mobileNumberIsRequiredAndMustBeValid'),
    })
    .trim()
    .max(
      TD_MOBILE_PHONE_FIELD_LENGTH,
      t('td.errors.maxLength', {
        maxLength: TD_MOBILE_PHONE_FIELD_LENGTH,
      }),
    )
    .refine(
      value => !!value.match(/^\+[[0-9]*$/g),
      t('td.errors.mobileNumberIsRequiredAndMustBeValid'),
    );

export const nationalPhoneNumberSchema = (t: TFunction) =>
  z
    .string({
      required_error: t('td.errors.mobileNumberIsRequiredAndMustBeValid'),
      invalid_type_error: t('td.errors.mobileNumberIsRequiredAndMustBeValid'),
    })
    .trim()
    .min(DEFAULT_MIN_STRING_LENGTH, t('td.errors.mobileNumberIsRequiredAndMustBeValid'))
    .max(
      TD_NATIONAL_PHONE_LENGTH,
      t('td.errors.maxLength', {
        maxLength: TD_NATIONAL_PHONE_LENGTH,
      }),
    )
    .refine(value => !!value.match(/^\d+$/g), t('td.errors.mobileNumberIsRequiredAndMustBeValid'));

export const countryCodeSchema = (t: TFunction) =>
  z
    .string({
      required_error: t('td.errors.mobileNumberIsRequiredAndMustBeValid'),
      invalid_type_error: t('td.errors.mobileNumberIsRequiredAndMustBeValid'),
    })
    .trim()
    .min(DEFAULT_MIN_STRING_LENGTH, t('td.errors.mobileNumberIsRequiredAndMustBeValid'))
    .max(
      TD_COUNTRY_CODE_LENGTH,
      t('td.errors.maxLength', {
        maxLength: TD_COUNTRY_CODE_LENGTH,
      }),
    )
    .refine(
      value => !!value.match(/^\+\d{1,4}$/g),
      t('td.errors.mobileNumberIsRequiredAndMustBeValid'),
    );

export const emailAddressSchema = (t: TFunction) =>
  z
    .string({
      required_error: t('td.errors.emailAddressIsRequiredAndMustBeValid'),
      invalid_type_error: t('td.errors.emailAddressIsRequiredAndMustBeValid'),
    })
    .trim()
    .email(t('td.errors.emailAddressIsRequiredAndMustBeValid'))
    .max(
      TD_FIELD_MAX_LENGTH,
      t('td.errors.maxLength', {
        maxLength: TD_FIELD_MAX_LENGTH,
      }),
    );

export const tdGicFormSchema = (t: TFunction) =>
  z.object({
    prefix: z.nativeEnum(TdPrefix),
    firstName: nameSchema(t),
    lastName: nameSchema(t),
    mobileNumber: phoneNumberSchema(t),
    emailAddress: emailAddressSchema(t),
    countryOfResidence: z.nativeEnum(TdSupportedCountriesOfResidence),
    preferredLanguage: z.nativeEnum(Language),
    isConsentToApplyboardTermsAndConditions: z.literal(true),
    isAgreeToApplyBoardCommunications: z.boolean(),
    dateOfBirth: z.string().trim().nonempty(),
    confirmDateOfBirth: z.string().trim().nonempty(),
    isAgreeToTdAgreements: z.literal(true),
    nationalPhoneNumber: nationalPhoneNumberSchema(t),
    countryCallingCode: countryCodeSchema(t),
  });
